import React, { useState, useMemo } from 'react';
import { PencilSimple, Trash, Plus, Warning } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';

const LANG_OPTIONS = ['Gujarati', 'Hindi', 'English', 'Marathi', 'Rajasthani'];

const EMPTY = {
  name: '',
  phone: '',
  alternate_phone: '',
  license_number: '',
  license_expiry: '',
  experience_years: '0',
  languages: ['Gujarati', 'Hindi'],
  assigned_vehicle_id: '',
  address: '',
  photo_url: '',
  aadhar_number: '',
  status: 'available',
  notes: '',
};

const StatCard = ({ label, value }) => (
  <div className="bg-white border border-[#EBEBEB] rounded-[10px] px-5 py-3.5">
    <div className="text-2xl font-bold text-[#1B2D5E] font-body">{value}</div>
    <div className="text-xs text-[#888] font-body mt-0.5">{label}</div>
  </div>
);

const initials = (name) =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
};

const expiryClass = (dateStr) => {
  const days = daysUntil(dateStr);
  if (days === null) return 'text-gray-500';
  if (days < 30) return 'text-red-600 font-semibold';
  if (days < 90) return 'text-orange-600';
  return 'text-gray-500';
};

const DriversTab = ({ drivers, vehicles, onRefresh, onEditDriver }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const activeVehicles = useMemo(
    () => vehicles.filter((v) => v.status === 'active'),
    [vehicles],
  );

  const stats = useMemo(
    () => ({
      total: drivers.length,
      active: drivers.filter((d) => d.status === 'available' || d.status === 'on_trip').length,
      onTrip: drivers.filter((d) => d.status === 'on_trip').length,
      available: drivers.filter((d) => d.status === 'available').length,
    }),
    [drivers],
  );

  const licenseWarning = daysUntil(form.license_expiry) !== null && daysUntil(form.license_expiry) < 30;

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setModalOpen(true);
  };

  const openEdit = (d) => {
    setEditing(d);
    setForm({
      name: d.name,
      phone: d.phone,
      alternate_phone: d.alternate_phone || '',
      license_number: d.license_number || '',
      license_expiry: d.license_expiry || '',
      experience_years: String(d.experience_years ?? 0),
      languages: d.languages?.length ? d.languages : ['Gujarati', 'Hindi'],
      assigned_vehicle_id: d.assigned_vehicle_id || '',
      address: d.address || '',
      photo_url: d.photo_url || '',
      aadhar_number: d.aadhar_number || '',
      status: d.status || 'available',
      notes: d.notes || '',
    });
    setModalOpen(true);
  };

  React.useEffect(() => {
    if (onEditDriver) onEditDriver.current = openEdit;
  }, [onEditDriver]);

  const toggleLang = (lang) => {
    setForm((p) => ({
      ...p,
      languages: p.languages.includes(lang)
        ? p.languages.filter((l) => l !== lang)
        : [...p.languages, lang],
    }));
  };

  const save = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Name and phone are required');
      return;
    }
    const now = new Date().toISOString();
    const row = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      alternate_phone: form.alternate_phone.trim() || null,
      license_number: form.license_number.trim() || null,
      license_expiry: form.license_expiry || null,
      experience_years: parseInt(form.experience_years, 10) || 0,
      languages: JSON.stringify(form.languages),
      assigned_vehicle_id: form.assigned_vehicle_id || null,
      address: form.address.trim() || null,
      photo_url: form.photo_url.trim() || null,
      aadhar_number: form.aadhar_number.trim() || null,
      status: form.status,
      notes: form.notes.trim() || null,
      is_available: form.status === 'available',
      updated_at: now,
    };
    try {
      if (editing) {
        const { error } = await supabase.from('drivers').update(row).eq('id', editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('drivers')
          .insert({ ...row, id: crypto.randomUUID(), created_at: now });
        if (error) throw error;
      }
      toast.success('✓ Driver saved successfully');
      setModalOpen(false);
      onRefresh();
    } catch (err) {
      toast.error(err.message || 'Failed to save driver');
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.name}?`)) return;
    try {
      const { error } = await supabase.from('drivers').delete().eq('id', d.id);
      if (error) throw error;
      toast.success('Driver deleted');
      onRefresh();
    } catch {
      toast.error('Failed to delete driver');
    }
  };

  const statusBadge = (status) => {
    const map = {
      available: 'bg-green-100 text-green-800',
      on_trip: 'bg-blue-100 text-blue-800',
      inactive: 'bg-red-100 text-red-800',
      on_leave: 'bg-orange-100 text-orange-800',
    };
    const labels = {
      available: 'Available',
      on_trip: 'On Trip',
      inactive: 'Inactive',
      on_leave: 'On Leave',
    };
    return <Badge className={map[status] || 'bg-gray-100 text-gray-700'}>{labels[status] || status}</Badge>;
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-display text-[#1B2D5E]">Manage Drivers</h2>
          <p className="text-sm text-[#888] font-body mt-1">Professional driver team</p>
        </div>
        <button
          type="button"
          onClick={openAdd}
          className="inline-flex items-center gap-2 bg-[#1B2D5E] text-white rounded-lg px-5 py-2.5 text-sm font-medium font-body hover:bg-[#E8651A] transition-colors"
        >
          <Plus size={16} weight="bold" />
          Add Driver
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Drivers" value={stats.total} />
        <StatCard label="Active" value={stats.active} />
        <StatCard label="On Trip" value={stats.onTrip} />
        <StatCard label="Available" value={stats.available} />
      </div>

      <div className="bg-white border border-[#EBEBEB] rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="font-body text-xs">#</TableHead>
              <TableHead className="font-body text-xs">Photo</TableHead>
              <TableHead className="font-body text-xs">Name</TableHead>
              <TableHead className="font-body text-xs">Phone</TableHead>
              <TableHead className="font-body text-xs">License</TableHead>
              <TableHead className="font-body text-xs">Expiry</TableHead>
              <TableHead className="font-body text-xs">Experience</TableHead>
              <TableHead className="font-body text-xs">Languages</TableHead>
              <TableHead className="font-body text-xs">Vehicle</TableHead>
              <TableHead className="font-body text-xs">Status</TableHead>
              <TableHead className="font-body text-xs">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {drivers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center text-[#888] font-body py-8">
                  No drivers yet. Click &quot;Add Driver&quot; to get started.
                </TableCell>
              </TableRow>
            ) : (
              drivers.map((d, i) => {
                const expDays = daysUntil(d.license_expiry);
                return (
                  <TableRow key={d.id} className="font-body text-[13px] hover:bg-[#FAFAFA]" data-testid={`driver-row-${d.id}`}>
                    <TableCell className="py-3.5">{i + 1}</TableCell>
                    <TableCell>
                      {d.photo_url ? (
                        <img src={d.photo_url} alt={d.name} className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-[#1B2D5E] text-white text-[13px] font-semibold flex items-center justify-center">
                          {initials(d.name)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{d.name}</TableCell>
                    <TableCell>{d.phone}</TableCell>
                    <TableCell>{d.license_number || '—'}</TableCell>
                    <TableCell className={expiryClass(d.license_expiry)}>
                      {d.license_expiry || '—'}
                      {expDays !== null && expDays < 30 && ' ⚠️'}
                    </TableCell>
                    <TableCell>{d.experience_years} yrs</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[140px]">
                        {(d.languages || []).map((l) => (
                          <span key={l} className="text-[11px] bg-[#F0F0F0] text-[#555] rounded px-2 py-0.5">{l}</span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[120px] truncate">
                      {d.assigned_vehicle_label || <span className="text-gray-400">Unassigned</span>}
                    </TableCell>
                    <TableCell>{statusBadge(d.status)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(d)}><PencilSimple size={16} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(d)}><Trash size={16} className="text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto font-body">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? 'Edit Driver' : 'Add New Driver'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div>
              <Label>Full Name *</Label>
              <Input className="mt-1" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            </div>
            <div>
              <Label>Phone Number *</Label>
              <Input type="tel" className="mt-1" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
            </div>
            <div>
              <Label>Alternate Phone</Label>
              <Input type="tel" className="mt-1" value={form.alternate_phone} onChange={(e) => setForm((p) => ({ ...p, alternate_phone: e.target.value }))} />
            </div>
            <div>
              <Label>Experience (years) *</Label>
              <Input type="number" min="0" className="mt-1" value={form.experience_years} onChange={(e) => setForm((p) => ({ ...p, experience_years: e.target.value }))} />
            </div>
            <div>
              <Label>License Number</Label>
              <Input className="mt-1" value={form.license_number} onChange={(e) => setForm((p) => ({ ...p, license_number: e.target.value }))} />
            </div>
            <div>
              <Label>License Expiry Date</Label>
              <Input type="date" className="mt-1" value={form.license_expiry} onChange={(e) => setForm((p) => ({ ...p, license_expiry: e.target.value }))} />
              {licenseWarning && (
                <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                  <Warning size={14} /> License expiring soon!
                </p>
              )}
            </div>
            <div className="col-span-2">
              <Label className="mb-2 block">Languages Known</Label>
              <div className="flex flex-wrap gap-4">
                {LANG_OPTIONS.map((lang) => (
                  <label key={lang} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.languages.includes(lang)} onCheckedChange={() => toggleLang(lang)} />
                    {lang}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Assign Vehicle</Label>
              <Select value={form.assigned_vehicle_id || 'none'} onValueChange={(v) => setForm((p) => ({ ...p, assigned_vehicle_id: v === 'none' ? '' : v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {activeVehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.model}{v.registration_number ? ` (${v.registration_number})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="on_trip">On Trip</SelectItem>
                  <SelectItem value="on_leave">On Leave</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Aadhar Number</Label>
              <Input className="mt-1" value={form.aadhar_number} onChange={(e) => setForm((p) => ({ ...p, aadhar_number: e.target.value }))} placeholder="XXXX-XXXX-1234" />
            </div>
            <div className="col-span-2">
              <Label>Address</Label>
              <Textarea className="mt-1" value={form.address} onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))} rows={2} />
            </div>
            <div className="col-span-2">
              <Label>Photo URL</Label>
              <Input className="mt-1" value={form.photo_url} onChange={(e) => setForm((p) => ({ ...p, photo_url: e.target.value }))} placeholder="https://..." />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea className="mt-1" value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} rows={2} />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button className="bg-[#1B2D5E] hover:bg-[#E8651A]" onClick={save}>Save Driver</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriversTab;
